import { Box, Divider, TableCell, TableRow, Typography } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** COMPONENT */
import TableRowExport from 'component/Pdf/TableRowExport'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import { numberFormat, toBuddhistYear } from 'utils/app.utils'

/** STYLE */
import { colors } from 'constants/theme'
import * as UseStyled from 'features/report/useStyled'

import { UseSnCode } from 'utils/useSnCode'
interface DentistInterface {
  dentistId: number
  dentistFullname: string
}

interface FcComponent {
  page: number
  pageLimit: number
  rowCount: number
  sortBy: string
  sortType: string
  data: any
  onRequestSort: (sortBy: string, sortType: string) => void
  handleChangeRowsPerPage: (val: any) => void
  handleChangePage: (val: any) => void
  doctors: DentistInterface[]
  filter: any
  filterDateStart: string
  filterDateEnd: string
  componentRef: any
}

export default function RemunerationList(props: FcComponent) {
  const { t } = useTranslation()
  const [snType, setSnType] = useState(false)

  const handleSnCode = useCallback(() => {
    const fetchData = async () => {
      const sn = await UseSnCode()
      setSnType(sn)
    }

    fetchData()
  }, [snType])

  useEffect(() => {
    handleSnCode()
  }, [])

  const renderSalaryTable = (data: any) => {

    const items = _.chain(data)
      .map((i: any) => {
        i.dateFormat = toBuddhistYear(moment(i.date), 'DD/MM/YYYY')
        return i
      })
      .groupBy('dateFormat')
      .map((value: any, key: string) => ({
        date: key,
        data: value,
        sumData: {
          paidTx: numberFormat(_.sumBy(_.filter(value, { stage: 1 }), 'paidTx')),
          paidMed: numberFormat(_.sumBy(_.filter(value, { stage: 1 }), 'paidMed')),
          paidPd: numberFormat(_.sumBy(_.filter(value, { stage: 1 }), 'paidPd')),
          paidMat: numberFormat(_.sumBy(_.filter(value, { stage: 1 }), 'paidMat')),
          amountPaid: numberFormat(_.sumBy(value, 'amountPaid')),
          amountCash: numberFormat(_.sumBy(value, 'amountCash')),
          amountTransfer: numberFormat(_.sumBy(value, 'amountTransfer')),
          amountDebit: numberFormat(_.sumBy(value, 'amountDebit')),
          amountCredit: numberFormat(_.sumBy(value, 'amountCredit')),
          amountTrementRight: numberFormat(_.sumBy(value, 'amountTrementRight')),
          amountAdvance: numberFormat(_.sumBy(value, 'amountAdvance')),
          dfPaid: numberFormat(_.sumBy(value, 'dfPaid')),
          creditCost: numberFormat(_.sumBy(value, 'creditCost'), 3, 3),
          dfLostTreatment: numberFormat(_.sumBy(value, 'dfLostTreatment')),
          dfNet: numberFormat(_.sumBy(value, 'dfNet'))
        }
      }))
      .value()

    return items
  }

  const headCells = [
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.#NO', align: 'center', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.BILL_ID', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.PATIENT_CN_NUMBER', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.PATIENT_SN_CODE', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.PATIENT_NAME', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.TX', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.MED', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.PD', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.MAT', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.PAYMENT', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.CASH', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.TRANSFER', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.DEBIT', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.CREDIT', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.ADVANCE', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.DF', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.CREDIT_DF', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT_DF', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.NET_DF', align: 'right', class: 'text-nowrap' }
  ]

  const headLabCells = [
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.#NO', align: 'center', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.DOCTOR', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.PATIENT_CN_NUMBER', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.PATIENT_SN_CODE', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.PATIENT_NAME', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.DESCRIPTION', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.LAB_NAME', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.JOB', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'LAB Fee', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'DF LAB Fee', align: 'right', class: 'text-nowrap' }
  ]

  const headDFCells = [
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.#NO', align: 'center', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.DOCTOR', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.NET_PRICE', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'ค่าประกันมือ', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.SUM_PAID', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'DF', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.CREDIT_DF', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT_DF', align: 'right', class: 'text-nowrap' },
    // { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.TR_CREDIT_DF', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.SUM_DF', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'DF LAB', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'Net DF', align: 'right', class: 'text-nowrap' }
  ]

  const headTotalCells = [
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.INCOME', align: 'right', class: 'text-nowrap' }, //Income
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.NET_CLINIC', align: 'right', class: 'text-nowrap' }, //Net clinic
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.DF_NET', align: 'right', class: 'text-nowrap' }, //Net lab
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.SUM_DF', align: 'right', class: 'text-nowrap' }, //Dentist Free
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.DF_LAB', align: 'right', class: 'text-nowrap' }, //Df lab

    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.CASH', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.TRANSFER', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.DEBIT', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.CREDIT', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT', align: 'right', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION.ADVANCE', align: 'right', class: 'text-nowrap' }
  ]
  if (!snType) {
    headCells.splice(3, 1)
    headLabCells.splice(3, 1)
  }
  const renderData = (data: any, no: number, exportData: boolean, render: 'data' | 'sum') => {

    if (render === 'data') {
      const { billNo, stage, branchCnNumber, snCode, patientFullname, paidTx, paidMed, paidPd, amountPaid, amountCash, amountTransfer, amountDebit, amountCredit, amountTrementRight, amountAdvance, dfPaid, dfLostTreatment, creditCost, dfNet, paidMat } = data

      const num = exportData === true ? no + 1 : props.page * props.pageLimit - props.pageLimit + no + 1
      const objRenderData = {
        key: num.toString(),
        id: no.toString(),
        obj: data,
        columns: [
          { option: 'TEXT', align: 'center', label: num, class: 'text-nowrap' },
          {
            option: 'TEXT',
            align: 'left',
            label: <>
              {stage > 1 ? (
                <span>
                  {billNo} <span style={{ color: 'red' }}> {t('REPORT.TABLE.REMUNERATION.IS_PAY_OVERDUE')}</span>{' '}
                </span>
              ) : (
                billNo || '-'
              )}
              {(data?.isNhso === '1' ? <span style={{ color: '#2F4169', fontSize: exportData === true ? 7 : 12, fontWeight: "bold" }}>{` (สปสช.) `}</span> : '')}
            </>
            ,
            class: 'text-nowrap'
          },
          { option: 'TEXT', align: 'left', label: branchCnNumber || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', label: snCode || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', label: patientFullname || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(paidTx || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(paidMed || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(paidPd || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(paidMat || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(amountPaid || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(amountCash || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(amountTransfer || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(amountDebit || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(amountCredit || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(amountTrementRight || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(amountAdvance || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(dfPaid || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(creditCost || 0, 3, 3), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(dfLostTreatment || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(dfNet || 0), class: 'text-nowrap' }
        ]
      }
      if (!snType) {
        objRenderData.columns.splice(3, 1)
      }
      return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
    } else {
      if (data) {

        const objRenderData = {
          key: no.toString(),
          id: no.toString(),
          obj: data,

          columns: [
            { option: 'MERGE', align: 'center', colSpan: snType ? 4 : 3 },
            {
              option: 'TEXT',
              align: 'left',
              label: (
                <Typography className="text-right" sx={{ fontWeight: 500, fontSize: exportData ? 10 : '1rem' }}>
                  {t('REPORT.TITLE.SUM')}
                </Typography>
              ),
              class: 'text-nowrap'
            },
            { option: 'TEXT', align: 'right', label: data.paidTx, class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', label: data.paidMed, class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', label: data.paidPd, class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', label: data.paidMat, class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', label: data.amountPaid, class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', label: data.amountCash, class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', label: data.amountTransfer, class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', label: data.amountDebit, class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', label: data.amountCredit, class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', label: data.amountTrementRight, class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', label: data.amountAdvance, class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', label: data.dfPaid, class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', label: data.creditCost, class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', label: data.dfLostTreatment, class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', label: data.dfNet, class: 'text-nowrap' }
          ],
          classes: 'row-summary'
        }

        return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
      }
    }
  }

  const renderDataLab = (data: any, no: number, exportData: boolean, render: 'data' | 'sum') => {
    if (render === 'data') {
      const { dentist, branchCnNumber, snCode, patientFullname, remark, lab, operativeName, labFee, dfLabFee } = data
      const num = exportData === true ? no + 1 : props.page * props.pageLimit - props.pageLimit + no + 1
      const objRenderData = {
        key: num.toString(),
        id: no.toString(),
        obj: data,
        columns: [
          { option: 'TEXT', align: 'center', label: num, class: 'text-ellipsis' },
          { option: 'TEXT', align: 'left', label: dentist || '-', class: 'text-ellipsis' },
          { option: 'TEXT', align: 'left', label: branchCnNumber || '-', class: 'text-ellipsis' },
          { option: 'TEXT', align: 'left', label: snCode || '-', class: 'text-ellipsis' },
          { option: 'TEXT', align: 'left', label: patientFullname || '-', class: 'text-ellipsis' },
          { option: 'TEXT', align: 'left', label: remark || '-', class: 'text-ellipsis' },
          { option: 'TEXT', align: 'left', label: lab || '-', class: 'text-ellipsis' },
          { option: 'TEXT', align: 'left', label: operativeName || '-', class: 'text-ellipsis' },
          { option: 'TEXT', align: 'right', label: numberFormat(labFee || 0), class: 'text-ellipsis' },
          { option: 'TEXT', align: 'right', label: numberFormat(dfLabFee || 0), class: 'text-ellipsis' }
        ]
      }
      if (!snType) {
        objRenderData.columns.splice(3, 1)
      }
      return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
    } else {
      const objRenderData = {
        key: no.toString(),
        id: no.toString(),
        obj: data,
        columns: [
          { option: 'MERGE', align: 'center', colSpan: (snType ? 5 : 6) },
          {
            option: 'TEXT',
            align: 'left',
            label: (
              <Typography className="text-right" sx={{ fontWeight: 500, fontSize: exportData ? 10 : '1rem' }}>
                {t('REPORT.TITLE.SUM')}
              </Typography>
            ),
            class: 'text-nowrap'
          },
          { option: 'TEXT', align: 'right', label: numberFormat(_.sumBy(data, 'labFee')), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(_.sumBy(data, 'dfLabFee')), class: 'text-nowrap' }
        ],
        classes: 'row-summary'
      }

      return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
    }
  }

  const renderDataDF = (data: any, no: number, exportData: boolean, render: 'data' | 'sum') => {

    if (render === 'data') {
      const { doctorId, totalAmount, amountDf, dfCredit, dfLabFee, dfNet, dfLostCreditAndTreatment, dfLostTreatment, amountPaid, handInsurance } = data
      const num = exportData === true ? no + 1 : props.page * props.pageLimit - props.pageLimit + no + 1
      const totalDFLab = (Number(dfLostCreditAndTreatment) || 0) - (Number(dfNet) || 0)
      const newDFLab = totalDFLab < 0 ? 0 : totalDFLab
      const objRenderData = {
        key: num.toString(),
        id: no.toString(),
        obj: data,
        columns: [
          { option: 'TEXT', align: 'center', label: num, class: 'text-ellipsis' },
          { option: 'TEXT', align: 'left', label: _.get(_.find(props.doctors, { dentistId: Number(doctorId) }), 'dentistFullname') || '-', class: 'text-ellipsis' },
          { option: 'TEXT', align: 'right', label: numberFormat(totalAmount || 0), class: 'text-ellipsis' },
          { option: 'TEXT', align: 'right', label: numberFormat(handInsurance || 0), class: 'text-ellipsis' },
          { option: 'TEXT', align: 'right', label: numberFormat(amountPaid || 0), class: 'text-ellipsis' },
          { option: 'TEXT', align: 'right', label: numberFormat(amountDf || 0), class: 'text-ellipsis' },
          { option: 'TEXT', align: 'right', label: numberFormat(dfCredit || 0), class: 'text-ellipsis' },
          { option: 'TEXT', align: 'right', label: numberFormat(dfLostTreatment || 0), class: 'text-ellipsis' },
          { option: 'TEXT', align: 'right', label: numberFormat(dfLostCreditAndTreatment || 0), class: 'text-ellipsis' },
          { option: 'TEXT', align: 'right', label: numberFormat(newDFLab || 0), class: 'text-ellipsis' },
          { option: 'TEXT', align: 'right', label: numberFormat(parseFloat((dfNet || 0).toFixed(2)) || 0), class: 'text-ellipsis' }
        ]
      }

      return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
    } else {
      const totaSumlDFLab = data?.length ? data.map((item: any) => {
        const newSum = (item?.dfLostCreditAndTreatment || 0) - (parseFloat((item?.dfNet || 0).toFixed(2)))
        if (newSum < 0) {
          return 0
        } else {
          return newSum
        }
      }).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0)) : 0
      const newSumDFLab = totaSumlDFLab < 0 ? 0 : totaSumlDFLab
      const objRenderData = {
        key: no.toString(),
        id: no.toString(),
        obj: data,
        columns: [
          { option: 'TEXT', align: 'center', label: '' },
          {
            option: 'TEXT',
            align: 'left',
            label: (
              <Typography className="text-right" sx={{ fontWeight: 500, fontSize: exportData ? 10 : '1rem' }}>
                {t('REPORT.TITLE.SUM')}
              </Typography>
            ),
            class: 'text-nowrap'
          },
          { option: 'TEXT', align: 'right', label: numberFormat(_.sumBy(data, 'totalAmount')), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(_.sumBy(data, 'handInsurance')), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(_.sumBy(data, 'amountPaid')), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(_.sumBy(data, 'amountDf')), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(_.sumBy(data, 'dfCredit')), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(_.sumBy(data, 'dfLostTreatment')), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(_.sumBy(data, 'dfLostCreditAndTreatment')), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(newSumDFLab), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(_.sumBy(data, 'dfNet')), class: 'text-nowrap' }
        ],
        classes: 'row-summary'
      }

      return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
    }
  }

  const renderDataTotal = (exportData: boolean) => {
    let sumAmountCash = 0
    let sumAmountTransfer = 0
    let sumAmountDebit = 0
    let sumAmountCredit = 0
    let sumAmountTrementRight = 0
    let sumAmountAdvance = 0
    let sumLab = 0
    let sumAmountPaid = 0
    let sumDf = 0
    let dfNet = 0
    let dfLabFee = 0
    _.map(props.data.rows, (salary, indexSalary: number) => {
      sumAmountPaid += salary.table.map((amountPaidData: any) => amountPaidData.amountPaid).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0))
      sumAmountCash += salary.table.map((amountCashData: any) => amountCashData.amountCash).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0))
      sumAmountTransfer += salary.table.map((amountTransferData: any) => amountTransferData.amountTransfer).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0))
      sumAmountDebit += salary.table.map((amountDebitData: any) => amountDebitData.amountDebit).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0))
      sumAmountCredit += salary.table.map((amountCreditData: any) => amountCreditData.amountCredit).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0))
      sumAmountTrementRight += salary.table.map((amountTreatmentRightData: any) => amountTreatmentRightData.amountTrementRight).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0))
      sumAmountAdvance += salary.table.map((amountAdvanceData: any) => amountAdvanceData.amountAdvance).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0))
    })
    _.map(props.data.rowLab, (lab, indexLab: number) => {
      sumLab += lab.table.map((sumLabData: any) => sumLabData.labFee).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0))
    })

    if (props.data.rowDf !== undefined) {
      if (props.data.rowDf.length > 0) {
        sumDf += props.data.rowDf ? props.data.rowDf.map((rowDfData: any) => rowDfData.dfLostCreditAndTreatment).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0)) : 0
        dfNet += props.data.rowDf ? props.data.rowDf.map((rowDfData: any) => rowDfData.dfNet).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0)) : 0
        dfLabFee += props.data.rowDf ? props.data.rowDf.map((rowDfData: any) => rowDfData.dfLabFee).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0)) : 0
      }
    }

    const totaSumlDFLab = props.data.rowDf?.length ? props.data.rowDf.map((item: any) => {
      const newSum = (item?.dfLostCreditAndTreatment || 0) - (item?.dfNet || 0)
      if (newSum < 0) {
        return 0
      } else {
        return newSum
      }
    }).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0)) : 0
    const newSumDFLab = totaSumlDFLab < 0 ? 0 : totaSumlDFLab

    const netClinic = (sumAmountPaid - dfNet) < 0 ? 0 : (sumAmountPaid - dfNet)
    const objRenderData = {
      key: 'total',
      id: 'total',
      obj: [1],
      columns: [
        { option: 'TEXT', align: 'right', label: numberFormat(sumAmountPaid) }, //Income
        { option: 'TEXT', align: 'right', label: numberFormat(netClinic) }, // Net clinic
        { option: 'TEXT', align: 'right', label: numberFormat(dfNet) },
        // { option: 'TEXT', align: 'right', label: numberFormat(dfNet + dfLabFee) },
        { option: 'TEXT', align: 'right', label: props.data.rowDf?.length ? numberFormat(_.sumBy(props.data.rowDf, 'dfLostCreditAndTreatment')) : 0 },
        { option: 'TEXT', align: 'right', label: numberFormat(newSumDFLab) },
        { option: 'TEXT', align: 'right', style: { color: colors.blue }, label: numberFormat(sumAmountCash), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', style: { color: colors.blue }, label: numberFormat(sumAmountTransfer), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', style: { color: colors.blue }, label: numberFormat(sumAmountDebit), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', style: { color: colors.blue }, label: numberFormat(sumAmountCredit), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', style: { color: colors.blue }, label: numberFormat(sumAmountTrementRight), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', style: { color: colors.blue }, label: numberFormat(sumAmountAdvance) }
      ]
    }

    return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
  }

  const FILTER_TITLES = [
    { key: 'REPORT.FILTER.BRANCH', label: 'REPORT.FILTER.BRANCH' },
    { key: 'REPORT.FILTER.DATE_START', label: 'REPORT.FILTER.DATE_START' },
    { key: 'REPORT.FILTER.DATE_END', label: 'REPORT.FILTER.DATE_END' },
    { key: 'REPORT.FILTER.RECEIPT_TYPE', label: 'REPORT.FILTER.RECEIPT_TYPE' },
    { key: 'REPORT.FILTER.DOCTOR', label: 'REPORT.FILTER.DOCTOR' }
  ]

  const renderValue = (value: any, index: any) => {
    if (index === 1 || index === 2) {
      return toBuddhistYear(moment(value), 'DD/MM/YYYY')
    }
    return value
  }


  return (
    <>
      <Box>
        {_.map(props.data.rows, (salary: any, indexSalary: number) => {
          return (
            <>
              <Typography className={`${indexSalary > 0 ? 'mt-4' : ''} mb-1`} sx={{ fontWeight: 500 }}>
                <span className="pr-2">{t('REPORT.FILTER.DOCTOR')}</span>
                {_.get(_.find(props.doctors, { dentistId: Number(salary.doctorId) }), 'dentistFullname')}
                {salary.doctor}
              </Typography>
              <UseStyled.ResponsiveReportTable className="mt-2">
                <TableCustom
                  hidePagination
                  page={props.page}
                  pageLimit={props.pageLimit}
                  sortType={props.sortType}
                  sortBy={props.sortBy}
                  rowCount={props.rowCount}
                  onSort={props.onRequestSort}
                  setPageLimit={props.handleChangeRowsPerPage}
                  setPage={props.handleChangePage}
                  headCells={headCells}
                  rowsData={_.map(renderSalaryTable(salary.table), (d: any, index: number) => {
                    return (
                      <>
                        <TableRow>
                          <TableCell colSpan={_.size(headCells)} sx={{ fontWeight: '500 !important' }}>
                            <span className="mr-2">{t('MOMENT_AT')}</span>
                            {d.date}
                          </TableCell>
                        </TableRow>
                        {_.map(d.data, (dataTable, indexTable: number) => {
                          return renderData(dataTable, indexTable, false, 'data')
                        })}
                        {renderData(d.sumData, 0, false, 'sum')}
                      </>
                    )
                  })}
                  customScroll
                />
              </UseStyled.ResponsiveReportTable>
            </>
          )
        })}
      </Box>
      <Divider className="mx-0 mt-4 mb-0" sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }} />

      <Box className="mt-3">
        <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>LAB Report</Typography>
        <Typography className="mt-2">
          <span className="mr-2">{t('MOMENT_AT')}</span>
          {toBuddhistYear(moment(props.filterDateStart), 'DD/MM/YYYY')}
          <span className="mx-1">-</span>
          {toBuddhistYear(moment(props.filterDateEnd), 'DD/MM/YYYY')}
        </Typography>
        <UseStyled.ResponsiveReportTable className="mt-2">
          <TableCustom
            hidePagination
            page={props.page}
            pageLimit={props.pageLimit}
            sortType={props.sortType}
            sortBy={props.sortBy}
            rowCount={props.rowCount}
            onSort={props.onRequestSort}
            setPageLimit={props.handleChangeRowsPerPage}
            setPage={props.handleChangePage}
            headCells={headLabCells}
            customScroll
            rowsData={_.map(props.data.rowLab, (d: any, index: number) => {
              return (
                <>
                  <TableRow>
                    <TableCell colSpan={_.size(headLabCells)} sx={{ fontWeight: '500 !important' }}>
                      <span className="mr-2">{t('MOMENT_AT')}</span>
                      {toBuddhistYear(moment(d.date), 'DD/MM/YYYY')}
                    </TableCell>
                  </TableRow>
                  {_.map(d.table, (dataTable, indexTable: number) => {
                    return renderDataLab(dataTable, indexTable, false, 'data')
                  })}
                  {renderDataLab(d.table, 0, false, 'sum')}
                </>
              )
            })}
          />
        </UseStyled.ResponsiveReportTable>
      </Box>
      <Divider className="mx-0 mt-4 mb-0" sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }} />

      <Box className="mt-3">
        <Typography sx={{ fontSize: '20px', fontWeight: 500 }}>{t(`REPORT.TABLE.REMUNERATION.DENTIST_FREE`)}</Typography>
        <Typography className="mt-2">
          <span className="mr-2">{t('MOMENT_AT')}</span>
          {toBuddhistYear(moment(props.filterDateStart), 'DD/MM/YYYY')}
          <span className="mx-1">-</span>
          {toBuddhistYear(moment(props.filterDateEnd), 'DD/MM/YYYY')}
        </Typography>
        <UseStyled.ResponsiveReportTable className="mt-2">
          <TableCustom
            hidePagination
            page={props.page}
            pageLimit={props.pageLimit}
            sortType={props.sortType}
            sortBy={props.sortBy}
            rowCount={props.rowCount}
            onSort={props.onRequestSort}
            setPageLimit={props.handleChangeRowsPerPage}
            setPage={props.handleChangePage}
            headCells={headDFCells}
            rowsData={
              !_.isEmpty(props.data.rowDf)
                ? [
                  _.map(props.data.rowDf, (d: any, indexTable: number) => {
                    return renderDataDF(d, indexTable, false, 'data')
                  }),
                  renderDataDF(props.data.rowDf, 0, false, 'sum')
                ]
                : []
            }
            customScroll
          />
        </UseStyled.ResponsiveReportTable>
      </Box>
      <Divider className="mx-0 mt-4 mb-0" sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }} />

      <Box className="mt-3">
        <Typography sx={{ fontSize: '20px', fontWeight: 500 }}>Total Income Summary</Typography>
        <UseStyled.ResponsiveReportTable className="mt-2">
          <TableCustom
            hidePagination
            page={props.page}
            pageLimit={props.pageLimit}
            sortType={props.sortType}
            sortBy={props.sortBy}
            rowCount={props.rowCount}
            onSort={props.onRequestSort}
            setPageLimit={props.handleChangeRowsPerPage}
            setPage={props.handleChangePage}
            headCells={headTotalCells}
            rowsData={[renderDataTotal(false)]}
            customScroll
          />
        </UseStyled.ResponsiveReportTable>
      </Box>


      <Box id="print-table-container" ref={props.componentRef} style={{ overflowX: 'hidden', padding: 0 }}>
        <Typography fontSize="14px" fontWeight={400}>{t('REPORT.REPORT_NAME.REMUNERATION')}</Typography>
        <Box style={{ display: 'flex', flexDirection: "row", marginTop: "10px", whiteSpace: "nowrap" }}>
          {FILTER_TITLES.map((filter, index) => (
            <div key={index} style={{ display: "flex", flexDirection: "row", marginBottom: "16px" }}>
              <Typography style={{ fontSize: "9.5px", fontWeight: 700 }} >{t(filter.label)}:  &nbsp;</Typography>
              <Typography style={{ fontSize: "9.5px" }}>{renderValue(props.filter[index], index)}</Typography>
            </div>
          ))}
        </Box>

        <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ fontSize: '9.5px', fontWeight: 700 }}>
              <th style={{ textAlign: 'center', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.#NO')}</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid black' }} >{t('REPORT.TABLE.REMUNERATION.BILL_ID')}</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.PATIENT_CN_NUMBER')}</th>
              {snType && <th style={{ textAlign: 'left', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.PATIENT_SN_CODE')}</th>}
              <th style={{ textAlign: 'left', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.PATIENT_NAME')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.TX')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.MED')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.PD')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.MAT')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.PAYMENT')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.CASH')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.TRANSFER')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.DEBIT')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.CREDIT')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.ADVANCE')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.DF')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.CREDIT_DF')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT_DF')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.NET_DF')}</th>
            </tr>

          </thead>
          <tbody>
            {_.map(props.data.rows, (salary) => {
              return (
                <>
                  {_.map(renderSalaryTable(salary.table), (d: any) => {
                    return (
                      <>
                        <tr key={salary.doctorId}>
                          <td colSpan={_.size(headCells)} style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', width: '100px', verticalAlign: 'top' }}>
                            {t('REPORT.FILTER.DOCTOR')}
                            {_.get(_.find(props.doctors, { dentistId: Number(salary.doctorId) }), 'dentistFullname')}
                            {salary.doctor}
                          </td>
                        </tr>
                        <tr key={salary.doctorId}>
                          <td colSpan={_.size(headCells)} style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', width: '100px', verticalAlign: 'top' }}>
                            {t('MOMENT_AT')}
                            {d.date}
                          </td>
                        </tr>
                        {_.map(d.data, (dataTable, indexTable: number) => {
                          return renderData(dataTable, indexTable, true, 'data')
                        })}
                        {renderData(d.sumData, 0, true, 'sum')}
                      </>
                    )
                  })}
                </>
              )
            })}
            {!props.data?.rows?.length && (
              <tr>
                <td colSpan={_.size(headCells)} style={{ fontSize: 10, textAlign: 'center' }}>
                  {t('REPORT.TITLE.NO_DATA')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <br></br>

        <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse' }} >
          <thead >
            <tr style={{ fontSize: '9.5px', fontWeight: 700 }}>
              <th style={{ textAlign: 'center', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.#NO')}</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid black' }} >{t('REPORT.TABLE.REMUNERATION.DOCTOR')}</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.PATIENT_CN_NUMBER')}</th>
              {snType && <th style={{ textAlign: 'left', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.PATIENT_SN_CODE')}</th>}
              <th style={{ textAlign: 'left', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.PATIENT_NAME')}</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.DESCRIPTION')}</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.LAB_NAME')}</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.JOB')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('LAB Fee')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('DF LAB Fee')}</th>
            </tr>
          </thead>
          <tbody>
            {_.map(props.data.rowLab, (d: any) => {
              return (
                <>
                  <tr>
                    <td colSpan={_.size(headLabCells)} style={{ fontWeight: 500, fontSize: 10, border: 0, textAlign: 'left' }}>
                      {t('MOMENT_AT')}
                      {toBuddhistYear(moment(d.date), 'DD/MM/YYYY')}
                    </td>
                  </tr>
                  {_.map(d.table, (dataTable, indexTable: number) => {
                    return renderDataLab(dataTable, indexTable, true, 'data')
                  })}
                  {renderDataLab(d.table, 0, true, 'sum')}
                </>
              )
            })}

            {!props.data?.rowLab?.length && (
              <tr>
                <td colSpan={_.size(headLabCells)} style={{ fontSize: 10, textAlign: 'center' }}>
                  {t('REPORT.TITLE.NO_DATA')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <br></br>

        <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ fontSize: '9.5px', fontWeight: 700 }}>
              <th style={{ textAlign: 'center', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.#NO')}</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid black' }} >{t('REPORT.TABLE.REMUNERATION.DOCTOR')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.NET_PRICE')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('ค่าประกันมือ')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.SUM_PAID')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('DF')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.CREDIT_DF')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT_DF')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.SUM_DF')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('LAB Fee')}</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid black' }}>{t('DF LAB Fee')}</th>
            </tr>
          </thead>
          <tbody>
            {[
              _.map(props.data.rowDf, (d: any, indexTable: number) => {
                return renderDataDF(d, indexTable, true, 'data')
              }),
              renderDataDF(props.data.rowDf, 0, true, 'sum')
            ]}
          </tbody>
        </table>
        <br></br>

        <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'right', fontSize: '9.5px', fontWeight: 700 }}>
              <th style={{ borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.INCOME')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.NET_CLINIC')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.DF_NET')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.SUM_DF')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.DF_LAB')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.CASH')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.TRANSFER')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.DEBIT')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.CREDIT')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('REPORT.TABLE.REMUNERATION.ADVANCE')}</th>
            </tr>
          </thead>
          <tbody>
            {renderDataTotal(true)}
          </tbody>
        </table>
      </Box>
    </>
  )
}